import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { DocumentsSettingsRootPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import type { Tab } from 'modules/core/components/Card/types'
import Loading from 'modules/core/components/Loading'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import CollectionsBootstrap from 'modules/documents/lib/SettingsCollectionsBootstrap'

const Container = ({
  canManageEmployeesFolders,
  canManageFolders,
  canManagePublicCompanyFolders,
}: {
  canManageEmployeesFolders: boolean
  canManageFolders: boolean
  canManagePublicCompanyFolders: boolean
}) => {
  const { navigation } = useNavigation()
  const tabs = React.useMemo(() => {
    const items: Tab[] = []

    if (canManageEmployeesFolders) {
      items.push({
        name: i18n.t('settings.folders.tabs.employee'),
        href: navigation.settings.folders.employees.fullPath(),
      })
    }

    if (canManageFolders) {
      items.push({
        name: i18n.t('settings.folders.tabs.internal'),
        href: navigation.settings.folders.internal.fullPath(),
      })
    }

    if (canManagePublicCompanyFolders) {
      items.push({
        name: i18n.t('settings.folders.tabs.public_company'),
        href: navigation.settings.folders.public.fullPath(),
      })
    }

    items.push({
      name: i18n.t('settings.folders.tabs.custom_fields'),
      href: navigation.settings.folders.customFields.fullPath(),
    })

    return items
  }, [canManageEmployeesFolders, canManageFolders, canManagePublicCompanyFolders, navigation])

  const hasAccess = canManageFolders || canManageEmployeesFolders || canManagePublicCompanyFolders

  return (
    <AuthorizeUser authorizeFn={() => hasAccess}>
      <Outlet
        context={{
          tabs,
          canManageEmployeesFolders,
          canManageFolders,
          canManagePublicCompanyFolders,
        }}
      />
    </AuthorizeUser>
  )
}

const RootPage = () => {
  const { data, loading } = useQuery(DocumentsSettingsRootPageDocument)

  if (loading) return <Loading />

  const canManageEmployeesFolders =
    data?.documents._policies.companyManageEmployeesFolders.can ?? false
  const canManageFolders = data?.documents._policies.companyManageFolders.can ?? false
  const canManagePublicCompanyFolders =
    data?.documents._policies.companyManagePublicCompanyFolders.can ?? false

  return (
    <CollectionsBootstrap>
      <Container
        canManageEmployeesFolders={canManageEmployeesFolders}
        canManageFolders={canManageFolders}
        canManagePublicCompanyFolders={canManagePublicCompanyFolders}
      />
    </CollectionsBootstrap>
  )
}

export default RootPage
